import z from 'zod'
import { useCallback, useMemo, useState, type Dispatch, type SetStateAction } from 'react'
import { useQueryState, type ParserBuilder } from 'nuqs'
import { isDefined, isNotDefined } from '@terros/common'
import { parseAsZodSchema } from '../util/parseAs'
import { useStorageKey } from './useStorageKey'

type InputWithZodSchema<T extends NonNullable<unknown>> = {
  defaultValue: T
  storedKey: string
  urlKey: string
  schema: z.ZodSchema<T>
  clearOnDefault?: boolean
}

type Output<T extends NonNullable<unknown>> = {
  value: T
  setValue: Dispatch<SetStateAction<T>>
}

export function useStoredQuery<T extends NonNullable<unknown>>({
  defaultValue,
  storedKey,
  urlKey,
  schema,
  clearOnDefault = true,
}: InputWithZodSchema<T>): Output<T> {
  const resolvedStoredKey = useStorageKey(storedKey)
  const resolvedDefaultValue: T = useMemo(() => {
    try {
      const stored = window.localStorage.getItem(resolvedStoredKey)
      if (isDefined(stored)) {
        const parsed = JSON.parse(stored)
        const parseResult = schema.safeParse(parsed)
        if (parseResult.success) return parseResult.data
      }
      return defaultValue
    } catch {
      return defaultValue
    }
  }, [resolvedStoredKey, defaultValue, schema])

  const [value, _setValue] = useQueryState<T>(
    urlKey,
    parseAsZodSchema(schema).withDefault(resolvedDefaultValue).withOptions({ clearOnDefault })
  )

  const setValue: Dispatch<SetStateAction<T>> = useCallback(
    (input): void => {
      const newValue = input instanceof Function ? input(value) : input
      window.localStorage.setItem(resolvedStoredKey, JSON.stringify(newValue))
      _setValue(newValue)
    },
    [resolvedStoredKey, value, _setValue]
  )

  return useMemo(
    () => ({
      value,
      setValue,
    }),
    [value, setValue]
  )
}

type NullableInput<T extends NonNullable<unknown>> = {
  storedKey: string
  urlKey: string
  parser: ParserBuilder<T>
}

type NullableOutput<T extends NonNullable<unknown>> = {
  value: T | null
  setValue: Dispatch<SetStateAction<T | null>>
}

type StoredWrite<T> = {
  key: string
  value: T | null
}

export function useStoredNullableQuery<T extends NonNullable<unknown>>({
  storedKey,
  urlKey,
  parser,
}: NullableInput<T>): NullableOutput<T> {
  const resolvedStoredKey = useStorageKey(storedKey)
  const [urlValue, _setValue] = useQueryState<T>(urlKey, parser)
  const [lastWrite, setLastWrite] = useState<StoredWrite<T>>()

  const storedValue = useMemo(() => {
    if (lastWrite && lastWrite.key === resolvedStoredKey) return lastWrite.value
    return readStoredValue(resolvedStoredKey, parser)
  }, [lastWrite, resolvedStoredKey, parser])

  const value = urlValue ?? storedValue

  const setValue: Dispatch<SetStateAction<T | null>> = useCallback(
    (input): void => {
      const newValue = input instanceof Function ? input(value) : input
      if (isDefined(newValue)) window.localStorage.setItem(resolvedStoredKey, serializeStoredValue(newValue, parser))
      else window.localStorage.removeItem(resolvedStoredKey)
      setLastWrite({ key: resolvedStoredKey, value: newValue })
      _setValue(newValue)
    },
    [resolvedStoredKey, value, parser, _setValue]
  )

  return useMemo(
    () => ({
      value,
      setValue,
    }),
    [value, setValue]
  )
}

function readStoredValue<T extends NonNullable<unknown>>(key: string, parser: ParserBuilder<T>): T | null {
  try {
    const stored = window.localStorage.getItem(key)
    if (isNotDefined(stored)) return null
    return parser.parse(stored)
  } catch {
    return null
  }
}

function serializeStoredValue<T extends NonNullable<unknown>>(value: T, parser: ParserBuilder<T>): string {
  return parser.serialize ? parser.serialize(value) : String(value)
}

export const useStoredBoolean = ({
  defaultValue,
  storedKey,
  urlKey,
}: Omit<InputWithZodSchema<boolean>, 'schema'>): Output<boolean> => {
  return useStoredQuery({
    defaultValue,
    storedKey,
    urlKey,
    schema: z.boolean(),
  })
}
